// src/components/Popups/ManutencaoPopup.jsx
import React, { useState, useEffect, useCallback } from 'react';
import { FiX, FiPlusCircle, FiEdit2, FiCheckSquare, FiTrash2, FiTool, FiFileText, FiLoader, FiShoppingCart } from 'react-icons/fi';
import Swal from 'sweetalert2';
import axios from 'axios';
import ServicoPopup from './ServicoPopup';
import generateChecklistReport from './generateChecklistReport';
import generateOrdemServicoReport from './gerarOrdemDeServico';
import generateOrdemCompraReport from './generateOrdemCompraReport'; // ATUALIZADO
import { API_BASE_URL } from '../../../config';
import styles from './ManutencaoPopup.module.css';

const ManutencaoPopup = ({ vehicle, onClose }) => {
    const [servicos, setServicos] = useState([]);
    const [isLoading, setIsLoading] = useState(true);
    const [isServicoPopupOpen, setIsServicoPopupOpen] = useState(false);
    const [currentServico, setCurrentServico] = useState(null);
    const [selectedServicoId, setSelectedServicoId] = useState(null);

    const fetchServicos = useCallback(async () => {
        if (!vehicle?.id) return;
        setIsLoading(true);
        try {
            const token = localStorage.getItem('token');
            const response = await axios.get(`${API_BASE_URL}/api/veiculos/${vehicle.id}/servicos`, {
                headers: { 'Authorization': `Bearer ${token}` }
            });
            setServicos(response.data);
        } catch (error) {
            console.error("Erro ao buscar serviços:", error.response || error);
            Swal.fire('Erro!', 'Não foi possível carregar os serviços do servidor.', 'error');
        } finally {
            setIsLoading(false);
        }
    }, [vehicle?.id]);

    useEffect(() => {
        fetchServicos();
    }, [fetchServicos]);

    const handleSaveServico = async (savedServico) => {
        const token = localStorage.getItem('token');
        const config = { headers: { 'Authorization': `Bearer ${token}` } };
        
        try {
            if (savedServico.id) {
                await axios.put(`${API_BASE_URL}/api/servicos/${savedServico.id}`, savedServico, config);
            } else {
                await axios.post(`${API_BASE_URL}/api/veiculos/${vehicle.id}/servicos`, savedServico, config);
            }
            Swal.fire({ title: 'Salvo!', text: 'Serviço salvo com sucesso.', icon: 'success', timer: 1500, showConfirmButton: false });
            fetchServicos();
        } catch (error) {
            console.error("Erro ao salvar serviço:", error.response || error);
            const errorMessage = error.response?.data?.error || 'Não foi possível salvar o serviço.';
            Swal.fire('Erro!', errorMessage, 'error');
        }
    };

    const handleDeleteServico = (servicoId) => {
        Swal.fire({
            title: 'Tem certeza?',
            text: "Você realmente deseja excluir este item?",
            icon: 'warning',
            showCancelButton: true,
            confirmButtonColor: '#10b981',
            cancelButtonColor: '#9ca3af',
            confirmButtonText: 'Sim, excluir!',
            cancelButtonText: 'Cancelar'
        }).then(async (result) => {
            if (result.isConfirmed) {
                try {
                    const token = localStorage.getItem('token');
                    await axios.delete(`${API_BASE_URL}/api/servicos/${servicoId}`, { headers: { 'Authorization': `Bearer ${token}` } });
                    Swal.fire('Excluído!', 'O item foi removido.', 'success');
                    fetchServicos();
                    if(selectedServicoId === servicoId) setSelectedServicoId(null);
                } catch (error) {
                    Swal.fire('Erro!', 'Não foi possível excluir o item.', 'error');
                }
            }
        });
    };

    const handleGenerateChecklist = () => {
        const selectedServico = servicos.find(s => s.id === selectedServicoId);
        if (selectedServico && vehicle) {
            generateChecklistReport(vehicle, selectedServico);
        }
    };

    const fetchFornecedorAndGenerateReport = async (reportGenerator) => {
        const selectedServico = servicos.find(s => s.id === selectedServicoId);
        if (!selectedServico || !vehicle || !selectedServico.fornecedor_id) return;

        try {
            const token = localStorage.getItem('token');
            const response = await axios.get(`${API_BASE_URL}/api/fornecedores/${selectedServico.fornecedor_id}`, { 
                headers: { 'Authorization': `Bearer ${token}` } 
            });
            const fornecedor = response.data;
            
            if (fornecedor) {
                reportGenerator(vehicle, selectedServico, fornecedor);
            } else {
                throw new Error('Fornecedor não encontrado.');
            }
        } catch (error) {
             console.error("Erro ao buscar dados do fornecedor:", error);
             Swal.fire('Erro!', 'Não foi possível encontrar os dados do fornecedor para gerar o relatório.', 'error');
        }
    };

    const handleGenerateOS = () => {
        fetchFornecedorAndGenerateReport(generateOrdemServicoReport);
    };

    const handleGenerateOC = () => { // ATUALIZADO
        fetchFornecedorAndGenerateReport(generateOrdemCompraReport);
    };

    const handleOpenCreate = () => {
        setCurrentServico(null);
        setIsServicoPopupOpen(true);
    };

    const handleOpenEdit = (servico) => {
        setCurrentServico(servico);
        setIsServicoPopupOpen(true);
    };
    
    const renderFooterActions = () => {
        const selectedServico = servicos.find(s => s.id === selectedServicoId);
        const isItemSelected = !!selectedServico;
        const hasFornecedor = isItemSelected && !!selectedServico.fornecedor_id;

        return (
            <div className={styles.footerActions}>
                <button 
                    className={styles.footerButtonSecondary} 
                    onClick={handleGenerateChecklist}
                    disabled={!isItemSelected}
                    title={!isItemSelected ? "Selecione um item para gerar o checklist" : "Gerar relatório de checklist interno"}
                >
                    <FiFileText /> Checklist
                </button>
                <button 
                    className={styles.footerButtonSecondary} 
                    onClick={handleGenerateOS}
                    disabled={!hasFornecedor}
                    title={!isItemSelected ? "Selecione um item" : !hasFornecedor ? "Este serviço não possui um fornecedor vinculado" : "Gerar Ordem de Serviço"}
                >
                    <FiTool /> Ordem de Serviço
                </button>
                <button 
                    className={styles.footerButtonSecondary} 
                    onClick={handleGenerateOC} // ATUALIZADO
                    disabled={!hasFornecedor}
                    title={!isItemSelected ? "Selecione um item" : !hasFornecedor ? "Este serviço não possui um fornecedor vinculado" : "Gerar Ordem de Compra"}
                >
                    <FiShoppingCart /> Ordem de Compra 
                </button>
            </div>
        );
    };

    return (
        <>
            <div className={styles.popupOverlay}>
                <div className={styles.popupContent}>
                    <div className={styles.popupHeader}>
                        <h2>Serviços e Manutenções</h2>
                        <button onClick={onClose} className={styles.closeButton}><FiX /></button>
                    </div>
                     <div className={styles.popupBody}>
                        <div className={styles.actionsHeader}>
                            <h3><FiCheckSquare/> Serviços e Checklists Disponíveis</h3>
                            <button className={styles.createButton} onClick={handleOpenCreate}><FiPlusCircle /> Criar Novo</button>
                        </div>
                        <div className={styles.listContainer}>
                            {isLoading ? ( 
                                <div className={styles.statusState}>
                                    <FiLoader className={styles.loaderIcon} />
                                    <span>Carregando...</span>
                                </div> 
                            ) : servicos.length === 0 ? ( 
                                <div className={styles.statusState}>
                                    <FiFileText size={24} />
                                    <span>Nenhum serviço ou checklist criado.</span>
                                </div>
                            ) : (
                                servicos.map(servico => (
                                    <div key={servico.id} className={`${styles.checklistItem} ${selectedServicoId === servico.id ? styles.selected : ''}`} onClick={() => setSelectedServicoId(servico.id)}>
                                        <div className={styles.itemInfo}>
                                            {servico.fornecedor_id ? 
                                                <FiTool className={`${styles.itemIcon} ${styles.osIcon}`} title="Ordem de Serviço"/> : 
                                                <FiCheckSquare className={`${styles.itemIcon} ${styles.checklistIcon}`} title="Checklist Interno"/>
                                            }
                                            <span className={styles.checklistTitle}>{servico.titulo}</span>
                                        </div>
                                        <div className={styles.checklistActions}>
                                            <button className={styles.actionButton} title="Editar" onClick={(e) => { e.stopPropagation(); handleOpenEdit(servico); }}><FiEdit2 /></button>
                                            <button className={`${styles.actionButton} ${styles.deleteButton}`} title="Excluir" onClick={(e) => { e.stopPropagation(); handleDeleteServico(servico.id); }}><FiTrash2 /></button>
                                        </div>
                                    </div>
                                ))
                            )}
                        </div>
                    </div>
                    <div className={styles.popupFooter}>
                        {renderFooterActions()}
                    </div>
                </div>
            </div>
            {isServicoPopupOpen && (
                <ServicoPopup onClose={() => setIsServicoPopupOpen(false)} onSave={handleSaveServico} servico={currentServico} vehicle={vehicle} />
            )}
        </>
    );
};

export default ManutencaoPopup;